import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import BidFolder from "../components/BidFolder";
import VolatilityWidget from "../components/VolatilityWidget";
import DemoSeedButton from "../components/DemoSeedButton";
import MarketBriefingWidget from "../components/MarketBriefingWidget";
import { Plus, Inbox, Sparkles } from "lucide-react";

export default function Dashboard() {
  const [rfps, setRfps] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const load = useCallback(async () => {
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/rfps"),
        api.get("/stats"),
      ]);
      setRfps(r);
      setStats(s);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const tmr = setInterval(load, 8000);
    return () => clearInterval(tmr);
  }, [load]);

  const hasDemo = rfps.some(r => r.id?.startsWith("rfp_demo_"));

  return (
    <Layout sidebar={<>
      <VolatilityWidget />
      <MarketBriefingWidget />
      <div className="glass p-5" data-testid="quick-actions">
        <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-text-secondary mb-4">
          {t("dashboard.quickActions")}
        </div>
        <button onClick={() => navigate("/upload")} data-testid="sidebar-upload-btn"
          className="btn-tactile btn-amber w-full inline-flex items-center justify-center gap-2 mb-3">
          <Plus className="w-4 h-4" /> {t("dashboard.newRfp")}
        </button>
        <button onClick={() => navigate("/bid-bond")} data-testid="sidebar-bond-btn"
          className="btn-tactile w-full">
          {t("dashboard.manageBonds")}
        </button>
      </div>
    </>}>
      <div className="mb-8 flex justify-between items-end gap-4 flex-wrap">
        <div>
          <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-amber-500 mb-2">{t("dashboard.kicker")}</div>
          <h1 className="font-display text-5xl">{t("dashboard.title")}</h1>
          <p className="text-text-secondary mt-2">{t("dashboard.subtitle")}</p>
        </div>
        {hasDemo && (
          <DemoSeedButton variant="icon" onSeeded={load} />
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
        {[
          [t("dashboard.totalRfps"), stats.total_rfps ?? 0],
          [t("dashboard.activeBids"), stats.active_bids ?? 0],
          [t("dashboard.subRfqs"), stats.sub_rfqs ?? 0],
          [t("dashboard.vendorQuotes"), stats.vendor_quotes ?? 0],
        ].map(([k, v]) => (
          <div key={k} className="tray-inset p-4" data-testid={`stat-${k.toLowerCase().replace(/ /g, '-')}`}>
            <div className="font-mono-data text-[9px] tracking-[0.25em] uppercase text-text-secondary">{k}</div>
            <div className="font-display text-3xl mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="tray p-8" data-testid="executive-desk">
        {loading ? (
          <div className="text-center py-20 font-mono-data text-xs text-text-secondary animate-pulse">
            {t("common.loading")}
          </div>
        ) : rfps.length === 0 ? (
          <div className="text-center py-20">
            <Inbox className="w-12 h-12 text-text-secondary/40 mx-auto mb-4" />
            <h3 className="font-display text-2xl mb-2">{t("dashboard.emptyTitle")}</h3>
            <p className="text-text-secondary mb-6">{t("dashboard.emptySub")}</p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button onClick={() => navigate("/upload")} data-testid="empty-upload-btn" className="btn-tactile btn-amber">
                {t("dashboard.emptyCta")}
              </button>
              <DemoSeedButton onSeeded={load} />
            </div>
            <p className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary/60 mt-4 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-amber-500" /> Try it instantly with 4 polished demo bids
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rfps.map((r, i) => <BidFolder key={r.id} rfp={r} index={i} />)}
          </div>
        )}
      </div>
    </Layout>
  );
}
